import { put, takeEvery, takeLatest, call } from 'redux-saga/effects';
import cloneDeep from 'lodash/cloneDeep';
import isNull from 'lodash/isNull';

import { deserializeKeys, serializeKeys } from '@iso/lib/helpers/keyNormalizer';
import history from '@iso/lib/helpers/history';
import request from '@iso/lib/helpers/httpClient';
import tokenService from '@iso/lib/tokenService';
import { USER_SIGNUP, INVITE_SIGNUP } from './constants';
import * as CONSTANTS from './constants';
import * as ACTIONS from './actions';

function* startApp() {
  const token = tokenService.getToken() || null;
  if (isNull(token)) {
    yield put(ACTIONS.startAppWithoutToken());
    return;
  }

  try {
    tokenService.manageToken(token);
    const data = yield call(request, '/auth/profile/', 'GET', null, true);
    yield put(ACTIONS.startAppWithTokenSuccess(serializeKeys(data)));
  } catch (error) {
    tokenService.removeToken();
    yield put(ACTIONS.startAppWithTokenFail(error.code ?? error.error));
  }
}

function* signin({ payload }) {
  try {
    const data = yield call(request, '/token/', 'POST', payload, false);
    tokenService.manageToken(data.access);
    tokenService.saveRefreshToken(data.refresh);
    yield put(ACTIONS.signinSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.signinError(error));
  }
}

function* googleSignIn({ payload }) {
  try {
    const data = yield call(request, '/social/google/login', 'POST', payload, false);
    tokenService.manageToken(data.access);
    tokenService.saveRefreshToken(data.refresh);
    yield put(ACTIONS.googleSignInSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.googleSignInError(error));
  }
}

function* refreshToken({ payload }) {
  try {
    yield put(ACTIONS.refreshSuccess(serializeKeys(payload)));
  } catch (error) {
    yield put(ACTIONS.refreshError(error));
  }
}

function* signout() {
  localStorage.clear();
  history.push('/');
}

function* signup({ payload, signUpType, history }) {
  try {
    const body = cloneDeep(payload);
    if (signUpType === USER_SIGNUP) {
      const data = yield call(
        request,
        '/signup/',
        'POST',
        deserializeKeys(body),
        false
      );
      yield put(ACTIONS.signupSuccess(serializeKeys(data)));
    } else if (signUpType === INVITE_SIGNUP) {
      yield call(
        request,
        `/user_registration/${payload.id}/`,
        'PATCH',
        deserializeKeys(body),
        false
      );

      history.push('/login');
    }
  } catch (error) {
    yield put(ACTIONS.signupError(error));
  }
}

function* resendVerificationEmail({ payload }) {
  try {
    const data = yield call(
      request,
      '/resend_email/',
      'POST',
      deserializeKeys(payload),
      false
    );
    yield put(ACTIONS.resendVerificationEmailSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.resendVerificationEmailError(error));
  }
}

function* forgotPassword({ payload }) {
  try {
    const data = yield call(
      request,
      '/forgot_password/',
      'POST',
      deserializeKeys(payload),
      false
    );
    yield put(ACTIONS.forgotPasswordSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.forgotPasswordError(error));
  }
}

function* verifyResetPasswordToken({ token }) {
  try {
    const data = yield call(
      request,
      `/reset_password/${token}/`,
      'GET',
      null,
      false
    );
    yield put(ACTIONS.verifyResetPasswordTokenSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.verifyResetPasswordTokenError(error));
  }
}

function* resetPassword({ payload }) {
  try {
    const data = yield call(
      request,
      '/reset_password/',
      'POST',
      deserializeKeys(payload),
      false
    );
    yield put(ACTIONS.resetPasswordSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.resetPasswordError(error));
  }
}

function* registerCompany({ payload }) {
  try {
    const data = yield call(
      request,
      '/company_registration/',
      'POST',
      deserializeKeys(payload),
      false
    );
    yield put(ACTIONS.registerCompanySuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.registerCompanyFail(error));
  }
}

function* fetchCompanyDetailByEmail({ payload }) {
  try {
    const data = yield call(
      request,
      '/get_production_company_by_email/',
      'POST',
      deserializeKeys(payload),
      false
    );
    yield put(ACTIONS.fetchCompanyDetailByEmailSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.fetchCompanyDetailByEmailFail(error));
  }
}

function* acceptInvitation({ payload }) {
  try {
    const data = yield call(
      request,
      `/validation/user_signup_token/?token=${payload}`,
      'GET',
      null,
      false
    );
    yield put(ACTIONS.invitationAcceptSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.invitationAcceptError(error.error));
  }
}

function* acceptCompanyOwnerInvitation({ payload }) {
  try {
    const data = yield call(
      request,
      `/invite/verification/${payload}/`,
      'GET',
      null,
      false
    );
    yield put(ACTIONS.acceptCompanyOwnerInvitationSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.acceptCompanyOwnerInvitationError(error.error));
  }
}

function* verifyEmail({ token }) {
  try {
    const data = yield call(
      request,
      `/signup/verification/${token}/`,
      'GET',
      null,
      false
    );
    yield put(ACTIONS.verifyEmailSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.verifyEmailError(error.error));
  }
}

function* syncAuthUser() {
  try {
    const data = yield call(request, '/auth/profile/', 'GET', null, true);

    yield put(ACTIONS.syncAuthUserSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.syncAuthUserFail(error.code ?? error.error));
  }
}

function* verifyMemo({ payload }) {
  try {
    const data = yield call(
      request,
      '/validate_memo_invitation_token/',
      'GET',
      payload,
      false
    );
    yield put(ACTIONS.verifyMemoSuccess(serializeKeys(data)));
    window.location.href = data;
  } catch (error) {
    yield put(ACTIONS.verifyMemoFail(error));
  }
}

function* declineBusinessMembers({ companyId, memberId, token }) {
  try {
    const data = yield call(
      request,
      `/company/${companyId}/sila/member/${memberId}/${token}`,
      'GET',
      null,
      true
    );
    yield put(ACTIONS.declineBusinessMemberSuccess(serializeKeys(data)));
    window.location.href = '/';
  } catch (error) {
    setTimeout(() => {
      window.location.href = '/';
    }, 5000);
    yield put(ACTIONS.declineBusinessMemberFail(error));
  }
}

function* companySignOut({ companyId }) {
  try {
    const data = yield call(request, `/company/${companyId}/logout`, 'POST');
    yield put(ACTIONS.companySignOutSuccess(serializeKeys(data)));
  } catch (error) {
    yield put(ACTIONS.companySignOutFail(error));
  }
}

function* setUserPaymentPlan({ payload }) {
  try {
    const data = yield call(
      request,
      '/stripe/subscription/',
      'POST',
      payload,
      true
    );
    yield put(ACTIONS.setUserPaymentPlanSuccess(serializeKeys(data)));
    yield syncAuthUser();
  } catch (error) {
    yield put(ACTIONS.setUserPaymentPlanFail(error));
  }
}

export default function* authSaga() {
  yield takeLatest(CONSTANTS.START_WITH_TOKEN_REQUEST, startApp);
  yield takeLatest(CONSTANTS.SIGNIN_REQUEST, signin);
  yield takeLatest(CONSTANTS.GOOGLE_SIGNIN_REQUEST, googleSignIn);
  yield takeLatest(CONSTANTS.SIGNUP_REQUEST, signup);
  yield takeLatest(
    CONSTANTS.RESEND_VERIFICATION_EMAIL_REQUEST,
    resendVerificationEmail
  );
  yield takeLatest(CONSTANTS.FORGOT_PASSWORD_REQUEST, forgotPassword);
  yield takeEvery(
    CONSTANTS.VERIFY_RESET_PASSWORD_TOKEN_REQUEST,
    verifyResetPasswordToken
  );
  yield takeLatest(CONSTANTS.RESET_PASSWORD_REQUEST, resetPassword);
  yield takeLatest(CONSTANTS.REGISTER_COMPANY_REQUEST, registerCompany);
  yield takeLatest(
    CONSTANTS.FETCH_COMPANY_DETAIL_BY_EMAIL_REQUEST,
    fetchCompanyDetailByEmail
  );
  yield takeLatest(CONSTANTS.INVITATION_ACCEPT_REQUEST, acceptInvitation);
  yield takeLatest(
    CONSTANTS.ACCEPT_COMPANY_OWNER_INVITATION_REQUEST,
    acceptCompanyOwnerInvitation
  );
  yield takeEvery(CONSTANTS.VERIFY_EMAIL_REQUEST, verifyEmail);
  yield takeLatest(CONSTANTS.SIGNOUT, signout);

  yield takeLatest(CONSTANTS.SYNC_AUTH_USER_REQUEST, syncAuthUser);
  yield takeLatest(CONSTANTS.VERIFY_MEMO_REQUEST, verifyMemo);
  yield takeLatest(CONSTANTS.COMPANY_SIGN_OUT_REQUEST, companySignOut);
  yield takeLatest(CONSTANTS.REFRESH_REQUEST, refreshToken);
  yield takeLatest(
    CONSTANTS.DECLINE_BUSINESS_MEMBERS_REQUEST,
    declineBusinessMembers
  );
  yield takeEvery(
    CONSTANTS.SET_USER_PAYMENT_PLAN_REQUEST,
    setUserPaymentPlan
  );
}
